import React, { useEffect, useState } from 'react';
import { Typography, Spin, Alert } from 'antd';
import apiClient from '../../apis/ApiInterceptor';
import ConfirmCancelModal from '../../components/ConfirmCancelModal';
import { openNotification } from '../../components/notifications';
import { checkAutoPermissions } from '../../apis/featureApi';
import Features from '../../enums/features';
import { getHoursToCancel, getIsHoursToCancelFeatureOn, getIsPenalizationFeatureOn } from '../../config';
import { callCancelSession } from '../../apis/sessionsApi';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title } = Typography;

const ScheduledSessions = ({ userId, className, onSessionCancel }) => {
    const [userSessions, setUserSessions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [showCancellationButton, setShowCancellationButton] = useState(false);

    const isHoursToCancelFeatureOn = getIsHoursToCancelFeatureOn();
    const isPenalizationFeatureOn = getIsPenalizationFeatureOn();

    useEffect(() => {
        const checkAuthorization = async () => {
            const isAuthorized = await checkAutoPermissions(Features.SCHEDULED_SESSIONS, userId);
            if (!isAuthorized) {
                const features = JSON.parse(localStorage.getItem('user_features') || '{}');
                setIsAuthorized(features[Features.SCHEDULED_SESSIONS] || false);
                setShowCancellationButton(features[Features.CANCEL_OTHER_CLASSES] || false);
            }else{
                setIsAuthorized(true);
                setShowCancellationButton(true);
            }
            setLoading(false);
        };

        if (userId) {
            checkAuthorization();
        } else {
            setError("No se proporcionó un ID de usuario.");
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (isAuthorized) {
            const fetchUserSessions = async () => {
                try {
                    setLoading(true);
                    const response = await apiClient.get(`/users/${userId}/sessions`, {}); // TODO: move this to api
                    setUserSessions(response.data);
                    setLoading(false);
                } catch (err) {
                    setError("Error al cargar las sesiones del usuario.");
                    setLoading(false);
                }
            };

            fetchUserSessions();
        }
    }, [userId, isAuthorized]);

    if (loading) {
        return (
            <div className={`${className} text-center`}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${className} text-center`}>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    if (!isAuthorized) {
        return null;
    }

    if (!userSessions || userSessions.length === 0) {
        return (<></>);
    }

    const cancelSession = async (sessionId) => {
        const response = await callCancelSession(sessionId);
        if(response.success){
            setUserSessions(userSessions.filter((s) => s.id !== sessionId));
            onSessionCancel(); // Trigger refresh in parent component
            openNotification(
                "success",
                "",
                "Clase cancelada exitosamente."
            )
        } 
        else{
            openNotification(
                "error",
                "",
                "Error al cancelar la clase. Contacta a soporte si el problema persiste."
            )
        }
    }

    const handleCancelation = async (session) => {
        const hoursToCancel = getHoursToCancel();

        // Parse start date as UTC
        const startDate = dayjs.utc(session.start_date);

        const cancelLimit = startDate.subtract(hoursToCancel, 'hour');

        // Current time in UTC
        const now = dayjs.utc();

        if (now.isBefore(cancelLimit)) {
            cancelSession(session.id);
        } else {
            if (isPenalizationFeatureOn || isHoursToCancelFeatureOn) {
                setSelectedSession(session);
                setShowConfirmCancelModal(true);
            } else {
                cancelSession(session.id);
            }
        }
    };

    return (
        <div className={`col-11 ${className}`}>
            <Title level={3} className="text-center">Sesiones Agendadas</Title>
            {userSessions.map((session) => (
                <div key={session.id} className="themed-block text-center text-md-left d-md-flex p-3 mx-auto mb-3">
                    <div>
                        <Title level={4} className="d-block my-2">{session.name}</Title>
                        <p class="d-block my-1"><span className="font-weight-bold">Día:</span> {dayjs.utc(session.start_date).tz(dayjs.tz.guess()).format('dddd D MMMM')}</p>
                        <p class="d-block my-1"><span className="font-weight-bold">Hora:</span> {dayjs.utc(session.start_date).tz(dayjs.tz.guess()).format('h:mm A')}</p>
                        <p class="d-block my-1"><span className="font-weight-bold">Lugar: </span>{session.place}</p>
                        {session.kangoo_id && (
                            <p className="d-block my-1"><span className="font-weight-bold">Kangoo: </span>{session.SKU}</p>
                        )}
                    </div>
                    {showCancellationButton &&
                        <div class="ml-auto my-3">
                            <button type="button" class="btn btn-danger" onClick={() => handleCancelation(session)}>Cancelar</button>
                        </div>
                    }
                </div>
            ))}
            <ConfirmCancelModal
                show={showConfirmCancelModal}
                session={selectedSession}
                onClose={() => setShowConfirmCancelModal(false)}
                onConfirm={cancelSession}
            />
        </div>
    );
};

export default ScheduledSessions;
